import type { ContractDto } from "src/app/features/Contract/types/ContractDto";
import type { ContractTerminalEntity } from "src/app/features/Contract/types/subTypes/ContractTerminalEntity";
import { TerminalType } from "src/app/features/TerminalProfile/types/TerminalProfileType";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { store } from "../../../../core/setup/redux";
import { sitesTerminalsApi } from "./serverApi";
import type { TerminalTypeState } from "./types";

const TERMINAL_TYPE_MAP: { [K in keyof TerminalTypeState]: TerminalType[] } = {
    allIPT: [TerminalType.ISIPT, TerminalType.ISANDROID],
    allOPT: [TerminalType.ISOPT],
    allUnattended: [TerminalType.ISUNATTENDED],
    allEcommerce: [TerminalType.ISECOMMERCE],
    allEvcharger: [TerminalType.ISEVCHARGER],
};

/**
 * Computes a new selection based on which terminal-type toggles changed.
 * When a toggle transitions false→true, all terminals of that type are added.
 * When a toggle transitions true→false, all terminals of that type are removed.
 */
export const applyTerminalTypeSelection = (
    terminalList: ContractTerminalEntity[],
    currentState: TerminalTypeState,
    previousState: TerminalTypeState | undefined,
    currentSelection: ContractTerminalEntity[],
): ContractTerminalEntity[] => {
    let selection = [...currentSelection];

    for (const key of Object.keys(TERMINAL_TYPE_MAP) as (keyof TerminalTypeState)[]) {
        const prev = previousState?.[key];
        const curr = currentState[key];
        const types = TERMINAL_TYPE_MAP[key];

        if (curr && prev !== true) {
            // Select all terminals of this type that aren't already selected
            const toAdd = terminalList.filter(
                (t) =>
                    t.terminalType !== undefined && types.includes(t.terminalType as TerminalType) && !selection.some((s) => s.id === t.id),
            );
            selection = [...selection, ...toAdd];
        } else if (!curr && prev === true) {
            // Deselect all terminals of this type
            selection = selection.filter((t) => t.terminalType === undefined || !types.includes(t.terminalType as TerminalType));
        }
    }

    // Deduplicate by id
    return selection.filter((item, index, self) => self.findIndex((i) => i.id === item.id) === index);
};

/**
 * Reads the full site terminal list from the RTK Query cache.
 * The data was already fetched by ContractTerminalsTable's useLoadSitesTerminalsQuery,
 * so this is a synchronous read of cached data.
 */
export const getSiteTerminalsFromCache = (siteId?: string | number): ContractTerminalEntity[] => {
    const dataGroup = getAssignedDataGroup();
    if (!dataGroup?.code || !siteId) return [];

    const cacheSelector = sitesTerminalsApi.endpoints.loadSitesTerminals.select({
        dataGroupCode: dataGroup.code,
        siteId,
    });

    const cached = cacheSelector(store.getState());
    return (cached?.data?.items as ContractTerminalEntity[]) ?? [];
};

export type TerminalTypeFlags = Pick<ContractDto, "allIPT" | "allOPT" | "allUnattended" | "allEcommerce" | "allEvcharger">;

/**
 * Derives allIPT / allOPT / etc. flags by checking whether every terminal of that
 * type that exists on the terminals for that site is present in the selected terminals array.
 */
export const deriveAllFlags = (
    selectedTerminals: ContractTerminalEntity[] = [],
    siteTerminals: ContractTerminalEntity[] = [],
    selectedFlags?: TerminalTypeFlags,
): TerminalTypeFlags => {
    if (siteTerminals.length === 0) {
        return {
            allIPT: selectedFlags?.allIPT ?? false,
            allOPT: selectedFlags?.allOPT ?? false,
            allUnattended: selectedFlags?.allUnattended ?? false,
            allEcommerce: selectedFlags?.allEcommerce ?? false,
            allEvcharger: selectedFlags?.allEvcharger ?? false,
        };
    }

    const flags: Record<string, boolean> = {};

    for (const [key, types] of Object.entries(TERMINAL_TYPE_MAP)) {
        const allOfType = siteTerminals.filter((t) => t.terminalType !== undefined && types.includes(t.terminalType as TerminalType));
        flags[key] = allOfType.length > 0 && allOfType.every((t) => selectedTerminals.some((s) => s.id === t.id));
    }

    return flags as TerminalTypeFlags;
};
